import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Movie } from "@/types/movie";
import { useMovieContext } from "@/context";
import { useAuth } from "@/context/AuthContext";
import { getMovieEnriched } from "@/services/movie/universalMovieService";

interface UseMovieDataResult {
  movie: Movie | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Universal hook for fetching movie data by detail ID
 * Now uses the universal enrichment service for consistent metadata
 */
export function useMovieData(detailId: string | null): UseMovieDataResult {
  const { allMovies, addOrUpdateMovie } = useMovieContext();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: movie,
    isLoading,
    isError,
    error,
    refetch: queryRefetch
  } = useQuery({
    queryKey: ['movie', detailId],
    queryFn: async () => {
      if (!detailId) return null;

      console.log(`[useMovieData] Universal fetching for ID: ${detailId}`);

      // Parse the detail ID to get TMDB ID
      if (detailId.startsWith('tmdb_')) {
        const tmdbId = parseInt(detailId.substring(5), 10);
        
        if (isNaN(tmdbId)) {
          throw new Error(`Invalid TMDB ID: ${detailId}`);
        }

        // First, try to find in local library using camelCase tmdbId
        const existingMovie = allMovies.find(m => m.tmdbId === tmdbId);
        if (existingMovie) {
          console.log(`[useMovieData] Found in library: ${existingMovie.title}`);
          return existingMovie;
        }

        console.log(`[useMovieData] Using universal enrichment service for TMDB ID: ${tmdbId}`);
        
        // Use universal enrichment service for consistent complete metadata
        const fetchedMovie = await getMovieEnriched(tmdbId, user?.id);
        
        // Add to local cache
        if (fetchedMovie && addOrUpdateMovie) {
          console.log(`[useMovieData] Adding universally enriched movie to cache: ${fetchedMovie.title}`);
          addOrUpdateMovie(fetchedMovie);
        }
        
        return fetchedMovie;
      }

      // If we get here, it's an invalid ID format
      throw new Error(`Invalid movie ID format: ${detailId}`);
    },
    enabled: !!detailId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
    retryDelay: 1000,
    refetchOnWindowFocus: false
  });

  const refetch = () => {
    console.log(`[useMovieData] Manual refetch requested for: ${detailId}`);
    queryRefetch();
  };

  return {
    movie: movie || null,
    isLoading,
    isError,
    error: error as Error | null,
    refetch
  };
}

/**
 * Hook to prefetch movie data (useful for hover previews)
 */
export function usePrefetchMovieData() {
  const queryClient = useQueryClient();
  
  return (detailId: string) => {
    queryClient.prefetchQuery({
      queryKey: ['movie', detailId],
      staleTime: 5 * 60 * 1000
    });
  };
}
